import React, { useState, useMemo } from "react";
import { useLocation, useParams, useNavigate } from "react-router-dom";
import {
    Box, Typography, Paper, Button, IconButton, Tooltip, Snackbar, Alert,
    Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle,
    CircularProgress, Fab, TextField, InputAdornment,
    Chip, Card, CardContent, Divider, Grid
} from "@mui/material";
import {
    Add as AddIcon, Delete as DeleteIcon, Search as SearchIcon,
    ArrowBack as ArrowBackIcon, Visibility as VisibilityIcon,
    Engineering as EngineeringIcon, PlayArrow as PlayArrowIcon,
    CheckCircle as CheckCircleIcon, Cancel as CancelIcon,
    Description as DescriptionIcon, Edit as EditIcon, Group as GroupIcon
} from "@mui/icons-material";
import { DataGrid } from "@mui/x-data-grid";
import { format, parseISO } from "date-fns";

import { useAuthContext } from "../../../context/AuthProvider";
import { useMaintenances } from "../../../hooks/maintenance/useMaintenances";
import { useVehicleDetails } from "../../../hooks/vehicle/useVehicleDetails";
import VehicleMaintenanceForm from "./VehicleMaintenanceForm";
import MaintenanceDocsDialog from "../utils/MaintenanceDocsDialog";


const MaintenanceByVehicleDashboard = () => {
    const { vehId } = useParams();
    const navigate = useNavigate();
    const parsedVehId = Number(vehId);
    const isValidVehId = !isNaN(parsedVehId) && parsedVehId > 0;

    const { user } = useAuthContext();

    // Mantenimientos por vehículo (hook ajustado más abajo)
    const {
        maintenances,
        isLoading: isLoadingMaintenances,
        error: errorMaintenances,
        createMutation,
        updateMutation,
        deleteMutation,
    } = useMaintenances(isValidVehId ? parsedVehId : undefined);

    const {
        data: vehicle,
        isLoading: isLoadingVehicle,
        error: errorVehicle
    } = useVehicleDetails(isValidVehId ? parsedVehId : undefined);

    // UI
    const [formState, setFormState] = useState({ open: false, maintenance: null });
    const [alert, setAlert] = useState({ open: false, message: "", severity: "success" });
    const [confirmDialog, setConfirmDialog] = useState({ open: false, maintenance: null });
    const [searchTerm, setSearchTerm] = useState("");
    const [docsState, setDocsState] = useState({ open: false, row: null });

    // Partición: actual en curso vs historial
    const { currentMaintenance, historyMaintenances } = useMemo(() => {
        const list = maintenances || [];
        const ongoing = list.find(m => (m.manest || "").toUpperCase() !== "FINALIZADO");
        const history = list.filter(m => (m.manest || "").toUpperCase() === "FINALIZADO");
        return { currentMaintenance: ongoing || null, historyMaintenances: history };
    }, [maintenances]);

    const inMaintenance = !!currentMaintenance;

    const showAlert = (message, severity = "success") =>
        setAlert({ open: true, message, severity });

    // Crear / editar (form simple) -> Aquí solo creamos padre (vehículo)
    const handleOpenForm = (maintenance = null) => {
        if (inMaintenance && maintenance == null) {
            showAlert("No puede crear un nuevo mantenimiento si ya existe uno en curso.", "warning");
            return;
        }
        setFormState({ open: true, maintenance });
    };
    const handleCloseForm = () => setFormState({ open: false, maintenance: null });

    const buildPayload = (data, userEmpId) => {
        const payload = {
            ...data,
            // FECHAS -> ISO
            manfecini: data.manfecini ? new Date(data.manfecini).toISOString() : null,
            manfecfin: data.manfecfin ? new Date(data.manfecfin).toISOString() : null,
            // FK de vehículo
            vehid: parsedVehId,
        };

        // No enviar estos campos desde el form
        delete payload.manest;
        delete payload.costototal;

        // Auditoría
        if (userEmpId) {
            payload.crebyid = userEmpId; // para crear
            payload.updbyid = userEmpId; // para actualizar
        }

        return payload;
    };

    // Submit del form: convierte fechas a ISO, agrega vehid y crea
    const handleSubmit = (data) => {
        if (!user?.EmpID) {
            showAlert("Debe iniciar sesión para realizar esta acción.", "error");
            return;
        }

        const isEditing = Boolean(formState?.maintenance?.manid); // asumiendo "manid" es el PK
        const payload = buildPayload(data, user.EmpID);

        if (!isEditing) {
            // CREAR
            createMutation.mutate(payload, {
                onSuccess: () => {
                    showAlert("Mantenimiento creado correctamente");
                    handleCloseForm();
                },
                onError: (err) => showAlert(err?.message || "Error al crear", "error"),
            });
            return;
        }

        const manid = formState.maintenance.manid;

        updateMutation.mutate(
            // Ajusta la forma según tu hook (algunos usan { id, data }, otros { manid, ...payload })
            { id: manid, data: payload },
            {
                onSuccess: () => {
                    showAlert("Mantenimiento actualizado correctamente");
                    handleCloseForm();
                },
                onError: (err) => showAlert(err?.message || "Error al actualizar", "error"),
            }
        );
    };

    // Eliminar lógico
    const handleConfirmDelete = () => {
        if (!user?.EmpID) {
            showAlert("Debe iniciar sesión para realizar esta acción.", "error");
            setConfirmDialog({ open: false, maintenance: null });
            return;
        }
        deleteMutation.mutate(
            { id: confirmDialog.maintenance.manid, updbyid: user.EmpID },
            {
                onSuccess: () => {
                    showAlert("Mantenimiento eliminado lógicamente correctamente");
                    setConfirmDialog({ open: false, maintenance: null });
                },
                onError: (err) => showAlert(err?.message || "Error al eliminar", "error"),
            }
        );
    };

    const formatDateTime = (dateString) => {
        if (!dateString) return "—";
        try { return format(parseISO(dateString), "dd/MM/yyyy HH:mm"); }
        catch { return dateString; }
    };

    const getMaintenanceStatusChipColor = (status) => {
        switch ((status || "").toUpperCase()) {
            case "CREADO": return "info";
            case "INICIADO": return "primary";
            case "EN_PROCESO": return "warning";
            case "FINALIZADO": return "success";
            case "CANCELADO": return "error";
            default: return "default";
        }
    };
    const getMaintenanceStatusChipIcon = (status) => {
        switch ((status || "").toUpperCase()) {
            case "INICIADO":
            case "EN_PROCESO": return <PlayArrowIcon />;
            case "FINALIZADO": return <CheckCircleIcon />;
            case "CANCELADO": return <CancelIcon />;
            default: return null;
        }
    };

    const handleDocs = (row) => {
        setDocsState({ open: true, row });
    };

    // Búsqueda en historial
    const filteredHistoryMaintenances = useMemo(() => {
        const term = (searchTerm || "").toLowerCase();
        return (historyMaintenances || []).filter(item =>
            (item.tipomantenimientonombre || "").toLowerCase().includes(term) ||
            (item.manobs || "").toLowerCase().includes(term) ||
            (item.manest || "").toLowerCase().includes(term)
        );
    }, [historyMaintenances, searchTerm]);

    const columns = [
        { field: "manid", headerName: "ID", width: 90 },
        {
            field: "manfecini", headerName: "Inicio", width: 170,
            renderCell: (params) => formatDateTime(params.row.manfecini),
            valueGetter: (p) => p.row.manfecini
        },
        {
            field: "manfecfin", headerName: "Fin", width: 170,
            renderCell: (params) => formatDateTime(params.row.manfecfin),
            valueGetter: (p) => p.row.manfecfin
        },
        { field: "tipomantenimientonombre", headerName: "Tipo", flex: 1, minWidth: 160 },
        {
            field: "manest", headerName: "Estado", width: 140,
            renderCell: (params) => (
                <Chip size="small" label={params.row.manest} color={getMaintenanceStatusChipColor(params.row.manest)} />
            )
        },
        { field: "mankm", headerName: "KM", width: 100 },
        {
            field: "costototal", headerName: "Costo Total", width: 140,
            renderCell: (p) => p.row.costototal ? `$${Number(p.row.costototal).toFixed(2)}` : "—"
        },
        { field: "manobs", headerName: "Obs.", flex: 1, minWidth: 180 },
        {
            field: "actions", headerName: "Acciones", width: 140, sortable: false, filterable: false,
            renderCell: (params) => (
                <Box>
                    <Tooltip title="Editar (Componentes)">
                        <IconButton size="small" onClick={() => handleOpenForm(params.row)}>
                            <EditIcon color="primary" />
                        </IconButton>
                    </Tooltip>

                    <Tooltip title="Ver Detalles (Componentes)">
                        <IconButton
                            size="small"
                            onClick={() => navigate(`/operaciones/vehiculo/${vehicle.vehid}/mantenimiento/${params.row.manid}`)}
                        >
                            <VisibilityIcon color="action" />
                        </IconButton>
                    </Tooltip>

                    <Tooltip title="Ver Documentos (Componentes)">
                        <IconButton size="small" onClick={() => handleDocs(params.row)}>
                            <DescriptionIcon color="primary" />
                        </IconButton>
                    </Tooltip>
                </Box>
            )
        },
    ];

    // Estados de carga/error del vehículo
    if (!isValidVehId) {
        return (
            <Paper sx={{ p: 3, borderRadius: 2 }}>
                <Typography color="text.secondary" variant="h6">Vehículo inválido.</Typography>
                <Button onClick={() => navigate(-1)} startIcon={<ArrowBackIcon />}>Volver</Button>
            </Paper>
        );
    }
    if (isLoadingVehicle) {
        return (
            <Paper sx={{ p: 3, borderRadius: 2, display: "flex", justifyContent: "center", alignItems: "center", minHeight: 300 }}>
                <CircularProgress /><Typography sx={{ ml: 2 }}>Cargando vehículo…</Typography>
            </Paper>
        );
    }
    if (errorVehicle) {
        return (
            <Paper sx={{ p: 3, borderRadius: 2 }}>
                <Typography color="error" variant="h6">Error al cargar el vehículo.</Typography>
                <Button onClick={() => navigate(-1)} startIcon={<ArrowBackIcon />}>Volver</Button>
            </Paper>
        );
    }

    // Estados de carga/error de mantenimientos
    if (isLoadingMaintenances) {
        return (
            <Paper sx={{ p: 3, borderRadius: 2, display: "flex", justifyContent: "center", alignItems: "center", minHeight: 300 }}>
                <CircularProgress /><Typography sx={{ ml: 2 }}>Cargando mantenimientos…</Typography>
            </Paper>
        );
    }
    if (errorMaintenances) {
        return (
            <Paper sx={{ p: 3, borderRadius: 2 }}>
                <Typography color="error" variant="h6">Error al cargar mantenimientos: {errorMaintenances.message}</Typography>
                <Button onClick={() => navigate(-1)} startIcon={<ArrowBackIcon />}>Volver</Button>
            </Paper>
        );
    }

    return (
        <Paper sx={{ p: 3, borderRadius: 2 }}>
            {/* Header + buscar */}
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Box display="flex" alignItems="center" gap={1}>
                    <IconButton onClick={() => navigate(-1)}><ArrowBackIcon /></IconButton>
                    <Typography variant="h5">Mantenimientos del Vehículo</Typography>
                </Box>
                <TextField
                    variant="outlined" size="small" placeholder="Buscar en historial…"
                    value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
                    InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }}
                />
            </Box>

            {/* Resumen vehículo */}
            <Card variant="outlined" sx={{ mb: 3 }}>
                <CardContent>
                    <Typography variant="h6" gutterBottom>
                        Vehículo:{" "}
                        <Typography component="span" variant="h6" color="text.primary">
                            {vehicle.vehmat} {vehicle.vehmod ? `— ${vehicle.vehmod}` : ""} {vehicle.vehani ? `(${vehicle.vehani})` : ""}
                        </Typography>
                    </Typography>
                    <Typography variant="body2" color="text.secondary">ID: {vehicle.vehid}</Typography>
                </CardContent>
            </Card>

            {/* Mantenimiento en curso */}
            {currentMaintenance ? (
                <Card variant="outlined" sx={{ mb: 3, borderColor: "primary.main", borderWidth: 2 }}>
                    <CardContent>
                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                            <Typography variant="h6" color="primary.main">Mantenimiento en Curso</Typography>
                            <Box>

                                <Tooltip title="Editar (Componentes)">
                                    <IconButton size="small" onClick={() => handleOpenForm(currentMaintenance)}>
                                        <EditIcon color="primary" />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title="Ver Detalles (Componentes)">
                                    <IconButton size="small" onClick={() => navigate(`/operaciones/vehiculo/${vehicle.vehid}/mantenimiento/${currentMaintenance.manid}`)}>
                                        <VisibilityIcon color="primary" />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title="Ver Documentos (Componentes)">
                                    <IconButton size="small" onClick={() => handleDocs(currentMaintenance)}>
                                        <DescriptionIcon color="primary" />
                                    </IconButton>
                                </Tooltip>
                                {/*
                                <Tooltip title="Ver Personas (Componentes)">
                                    <IconButton size="small" onClick={() => navigate(`/operaciones/vehiculo/${vehicle.vehid}/mantenimiento/${currentMaintenance.manid}`)}>
                                        <GroupIcon color="primary" />
                                    </IconButton>
                                </Tooltip>
                                */}
                                {(currentMaintenance.manest === 'CREADO') && (
                                    <Tooltip title="Eliminar (lógica)">
                                        <IconButton size="small" onClick={() => setConfirmDialog({ open: true, maintenance: currentMaintenance })}>
                                            <DeleteIcon color="error" />
                                        </IconButton>
                                    </Tooltip>
                                )}
                            </Box>
                        </Box>
                        <Divider sx={{ mb: 2 }} />
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <Typography><strong>Tipo:</strong> {currentMaintenance.tipomantenimientonombre || "—"}</Typography>
                                <Typography sx={{ mt: 1 }}>
                                    <strong>Estado:</strong>{" "}
                                    <Chip
                                        label={currentMaintenance.manest}
                                        color={getMaintenanceStatusChipColor(currentMaintenance.manest)}
                                        size="small"
                                        icon={getMaintenanceStatusChipIcon(currentMaintenance.manest)}
                                        sx={{ ml: 1 }}
                                    />
                                </Typography>
                                <Typography sx={{ mt: 1 }}><strong>Fecha Inicio:</strong> {formatDateTime(currentMaintenance.manfecini)}</Typography>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Typography><strong>KM:</strong> {currentMaintenance.mankm ?? "—"}</Typography>
                                <Typography sx={{ mt: 1 }}><strong>Costo Estimado:</strong> {currentMaintenance.costototal ? `$${Number(currentMaintenance.costototal).toFixed(2)}` : "—"}</Typography>
                                <Typography sx={{ mt: 1 }}><strong>Obs.:</strong> {currentMaintenance.manobs || "—"}</Typography>
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>
            ) : (
                <Alert severity="info" sx={{ mb: 3 }}>
                    No hay mantenimiento en curso para este vehículo. Puedes crear uno nuevo.
                </Alert>
            )}

            {/* Historial */}
            <Typography variant="h6" mb={2}>Historial de Mantenimientos Finalizados</Typography>
            <Card variant="outlined">
                <Box sx={{ height: 520, width: "100%" }}>
                    <DataGrid
                        rows={filteredHistoryMaintenances}
                        columns={columns}
                        getRowId={(row) => row.manid}
                        disableRowSelectionOnClick
                        pageSizeOptions={[5, 10, 25]}
                        initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
                        loading={isLoadingMaintenances}
                        localeText={{
                            noRowsLabel: "Sin registros en el historial",
                        }}
                    />
                </Box>
            </Card>

            {/* FAB: crear mantenimiento (form simple) */}
            <Fab
                color="primary"
                sx={{ position: "fixed", bottom: 20, right: 20 }}
                onClick={handleOpenForm}
                disabled={inMaintenance}
            >
                <AddIcon />
            </Fab>

            {/* Modal: VehicleMaintenanceForm */}
            {formState.open && (
                <VehicleMaintenanceForm
                    open={formState.open}
                    handleClose={handleCloseForm}
                    onSubmit={handleSubmit}
                    defaultValues={{
                        ...formState.maintenance,
                        vehid: parsedVehId,
                        vehiculomatricula: vehicle?.vehmat,
                    }}
                />
            )}

            {/* Añadir documento al mantenimiento */}
            {docsState.open && (
                <MaintenanceDocsDialog
                    open={docsState.open}
                    onClose={() => setDocsState({ open: false, row: null })}
                    manId={docsState.row.manid}
                    row={docsState.row}
                    apiBase={process.env.REACT_APP_API_URL}
                />
            )}

            {/* Confirmación de eliminación */}
            <Dialog open={confirmDialog.open} onClose={() => setConfirmDialog({ open: false, maintenance: null })}>
                <DialogTitle>Confirmar eliminación de mantenimiento</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        ¿Seguro que desea eliminar lógicamente el mantenimiento (ID {confirmDialog.maintenance?.manid})?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmDialog({ open: false, maintenance: null })}>Cancelar</Button>
                    <Button onClick={handleConfirmDelete} color="error" variant="contained">Eliminar</Button>
                </DialogActions>
            </Dialog>

            {/* Alertas */}
            <Snackbar open={alert.open} autoHideDuration={4000} onClose={() => setAlert({ ...alert, open: false })}>
                <Alert severity={alert.severity} onClose={() => setAlert({ ...alert, open: false })}>
                    {alert.message}
                </Alert>
            </Snackbar>
        </Paper>
    );
};

export default MaintenanceByVehicleDashboard;
